import { SortOrder } from '@modules/common/types/sort-order';

/** One entry of the card's "Sort by" select, and the order that key starts in (as the list header menu does). */
export interface ViewSortOption {
  readonly key: string;
  readonly label: string;
  readonly natural: SortOrder;
}

/** What the card edits. `sort` is a {@link ViewSortOption} key or {@link DEFAULT_SORT}. */
export interface ViewSettingsValues {
  readonly sort: string;
  readonly reverse: boolean;
  readonly flaggedOnly: boolean;
  readonly pinnedOnly: boolean;
  readonly unreadOnly: boolean;
}

/** The part of an app's list state the card reads and writes; every `BaseStitchListState` has it. */
export interface ViewSettingsState {
  sortBy?: string;
  sortOrder?: SortOrder;
  flagged?: boolean;
  pinned?: boolean;
  unread?: boolean;
  readonly initialSort: { readonly by: string; readonly order: SortOrder };
  notifyStateChanged(): void;
}

export interface ViewSettingsSource {
  /** Lower-case noun for the card's hint: `email`, `event`, … */
  readonly noun: string;
  readonly sorts: readonly ViewSortOption[];
  /** Mail alone offers "Unread only". */
  readonly withUnread: boolean;
  /** The row is itself the Flagged view: "Flagged only" shows ticked, cannot be changed, and Apply never writes it. */
  readonly flaggedLocked: boolean;
  read(): ViewSettingsValues;
  apply(values: ViewSettingsValues): void;
}

export const DEFAULT_SORT = 'default';

const opposite = (order: SortOrder): SortOrder => (order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC);

/**
 * Binds the card to a list's own state: it reads the state's sort and filters and writes them back to the same
 * object, then notifies once, exactly as the list header's filter menu does.
 */
export function listStateViewSettings(
  state: ViewSettingsState,
  options: { noun: string; sorts: readonly ViewSortOption[]; withUnread?: boolean; flaggedLocked?: boolean },
): ViewSettingsSource {
  const initial = state.initialSort;
  const naturalOf = (key: string): SortOrder =>
    key === initial.by ? initial.order : (options.sorts.find((sort) => sort.key === key)?.natural ?? SortOrder.ASC);
  const sortValue = (): string => (!state.sortBy || state.sortBy === initial.by ? DEFAULT_SORT : state.sortBy);

  // A sort the menu does not offer (Relevance while a search runs) stays selectable, so opening the card never rewrites it.
  const current = sortValue();
  const known = current === DEFAULT_SORT || options.sorts.some((sort) => sort.key === current);
  const unlisted: ViewSortOption = {
    key: current,
    label: current === 'score' ? 'Relevance' : current,
    natural: SortOrder.DESC,
  };
  const sorts = known ? options.sorts : [...options.sorts, unlisted];

  const flaggedLocked = !!options.flaggedLocked;

  const read = (): ViewSettingsValues => ({
    sort: sortValue(),
    reverse: !!state.sortBy && state.sortOrder !== naturalOf(state.sortBy),
    flaggedOnly: flaggedLocked || !!state.flagged,
    pinnedOnly: !!state.pinned,
    unreadOnly: !!state.unread,
  });

  return {
    noun: options.noun,
    sorts,
    withUnread: !!options.withUnread,
    flaggedLocked,
    read,
    apply(values: ViewSettingsValues): void {
      const before = read();
      if (values.sort !== before.sort || values.reverse !== before.reverse) {
        const key = values.sort === DEFAULT_SORT ? initial.by : values.sort;
        state.sortBy = key;
        state.sortOrder = values.reverse ? opposite(naturalOf(key)) : naturalOf(key);
      }
      if (!flaggedLocked) state.flagged = values.flaggedOnly ? true : undefined;
      state.pinned = values.pinnedOnly ? true : undefined;
      if (options.withUnread) state.unread = values.unreadOnly ? true : undefined;
      state.notifyStateChanged();
    },
  };
}
